import { RcFile } from "antd/es/upload";

import { ISelectOption } from "shared/models";

import {
  ISalesRepSelectOptions,
  IUser,
  IUserPermissions,
  IUserRoleSelectOption,
} from "../models";
import { isArray, isObject } from "lodash";
import { http } from "shared/api/http";

const apiPath = `${process.env.REACT_APP_API_BASE_URL}/api/v1/users`;

export const getUser = async (id: string) => {
  const res = await http.get<IUser>(`${apiPath}/${id}`);
  return res?.data;
};

export const getCurrentUser = async () => {
  try {
    const res = await http.get<IUser>(`${apiPath}/me`);
    return res?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getSalesRepsSelectOptions = async () => {
  const res = await http.get<ISalesRepSelectOptions>(
    `${apiPath}/salesreps`
  );
  return res?.data;
};

export const updateUser = async (
  id: string,
  userToUpdate: IUser,
  file?: RcFile,
  _isCurrentUser = true
) => {
  try {
    const formData = new FormData();

    for (const key in userToUpdate) {
      if (userToUpdate.hasOwnProperty(key)) {
        formData.append(
          key,
          isArray((userToUpdate as any)[key])
            ? (userToUpdate as any)[key].join(",")
            : isObject((userToUpdate as any)[key])
              ? JSON.stringify((userToUpdate as any)[key])
              : (userToUpdate as any)[key]
        );
      }
    }

    if (file) {
      formData.append("profile_picture", file);
    }

    const res = await http.put<IUser>(
      `${apiPath}/${id}`,
      formData
    );
    return res?.data;
  } catch (error) {
    return Promise.reject(error);
  } 
};

export const addUser = async (user: IUser) => {
  const res = await http.post<IUser>(`${apiPath}`, user);
  return res?.data;
};

export const deleteUser = async (id: string) => {
  try {
    const res = await http.delete<string>(`${apiPath}/${id}`);
    return res?.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getUserSelectOptions = async (tenantId: string, organizationId: string) => {
  const res = await http.get<IUserRoleSelectOption[]>(
    `${buildUsersPath(tenantId, organizationId)}/select-options`
  );
  return res?.data;
};

export const getAuditorSelectOptions = async (tenantId: string, organizationId: string) => {
  const res = await http.get<IUserRoleSelectOption[]>(
    `${buildUsersPath(tenantId, organizationId)}/auditor-options`
  );
  return res?.data;
};

export const getAccountManagerSelectOptions = async (tenantId: string, organizationId: string) => {
  const res = await http.get<IUserRoleSelectOption[]>(
    `${buildUsersPath(tenantId, organizationId)}/account-manager-options`
  );
  return res?.data;
};

const buildUsersPath = (tenantId: string, organizationId: string) =>
  `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenants/${tenantId}/organizations/${organizationId}/users`;

export const getUserMentionOptions = async (tenantId: string, organizationId: string) => {
  const res = await http.get<ISelectOption[]>(
    `${buildUsersPath(tenantId, organizationId)}/mention-options`
  );
  return res?.data;
};

export const searchMentionOptions = async (tenantId: string, organizationId: string, query: string) => {
  const res = await http.get<ISelectOption[]>(
    `${buildUsersPath(tenantId, organizationId)}/mention-options/search?q=${encodeURIComponent(query)}`
  );
  return res?.data;
};

export const getOrganizationUser = async (tenantId: string, organizationId: string, userId: string) => {
  const res = await http.get(`${buildUsersPath(tenantId, organizationId)}/${userId}`);
  return res?.data;
};

export const updateOrganizationUserMetadata = async (
  tenantId: string,
  organizationId: string,
  userId: string,
  metadata: Record<string, any>
) => {
  const res = await http.put(`${buildUsersPath(tenantId, organizationId)}/${userId}`, {
    metadata,
  });
  return res?.data;
};

export const getTenantUser = async (userId: string, tenantId: string) => {
  const res = await http.get(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenants/${tenantId}/users/${userId}`
  );
  return res?.data;
};

export const updateTenantUser = async (
  userId: string,
  tenantId: string,
  profile: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string; company?: string; service?: string }
) => {
  const res = await http.put(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenants/${tenantId}/users/${userId}`,
    profile
  );
  return res?.data;
};

/**
 * Self-service profile update for the current user. Hits the /profile endpoint
 * gated by `update:profile` (granted to all roles), so non-admin users can edit
 * their own info without the admin-level `update:users` permission.
 */
export const updateOwnProfile = async (
  userId: string,
  tenantId: string,
  profile: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string; company?: string; service?: string }
) => {
  const res = await http.put(
    `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenants/${tenantId}/users/${userId}/profile`,
    profile
  );
  return res?.data;
};

export const getMyPermissions = async (tenantId: string, organizationId: string): Promise<IUserPermissions> => {
  const res = await http.get<IUserPermissions>(
    `${buildUsersPath(tenantId, organizationId)}/me/permissions`
  );
  return res?.data;
};

const buildTenantUsersPath = (tenantId: string) =>
  `${process.env.REACT_APP_API_BASE_URL}/api/v1/tenants/${tenantId}/users`;

export const getTenantUserSelectOptions = async (tenantId: string) => {
  const res = await http.get<IUserRoleSelectOption[]>(
    buildTenantUsersPath(tenantId)
  );
  return res?.data;
};

export const updateTenantUserRole = async (tenantId: string, userId: string, role: string) => {
  const res = await http.put(
    `${buildTenantUsersPath(tenantId)}/${userId}/role`,
    { role }
  );
  return res?.data;
};

export const removeTenantUser = async (tenantId: string, userId: string) => {
  await http.delete(`${buildTenantUsersPath(tenantId)}/${userId}`);
  return userId;
};

export const assignUserToOrganization = async (
  tenantId: string,
  organizationId: string,
  userId: string,
  role: string
) => {
  const res = await http.post(buildUsersPath(tenantId, organizationId), { userId, role });
  return res?.data;
};

export const removeOrganizationUser = async (
  tenantId: string,
  organizationId: string,
  userId: string
) => {
  await http.delete(`${buildUsersPath(tenantId, organizationId)}/${userId}`);
  return userId;
};

export const updateOrganizationUserRole = async (
  tenantId: string,
  organizationId: string,
  userId: string,
  role: string
) => {
  const res = await http.put(
    `${buildUsersPath(tenantId, organizationId)}/${userId}/role`,
    { role }
  );
  return res?.data;
};
